import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Button,
  Divider,
  Form,
  FormInstance,
  message,
  Select,
} from 'antd';
import { useTranslation } from 'react-i18next';
import ActionTypes from '@utils/deviceActions';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import rolesConstants from '@utils/rolesConstants';
import api from '@services/api';
import useSwr from '@hooks/useSwr';
import { IContract } from '@components/DeviceActionModal/types';
import SessionContext from '@providers/Session/SessionContext';
import CustomTag from '@components/CustomTag';
import { formatCurrency } from '@utils/formatters';
import { AxiosError } from 'axios';
import SelectCustomers from '@components/SelectCustomers';
import ActionsItems from '../..';

import * as S from './styles';

interface CancellationPenaltyPayloadProps {
  assetNumberParent: string;
  cancelAllChildren: boolean; // true = total, false = partial
  msisdnList?: string[];
}

interface CancellationPenaltyResponse {
  protocol: string;
  penalty?: {
    loyaltyStartDate: string; // '01-01-2021';
    loyaltyEndDate: string; // '31-12-2021';
    calculationBaseDate: string; // '09-06-2021';
    penaltyAmount: number;
  };
}

interface CancellationPayloadProps {
  protocol: string;
  cancellationType?: string;
}

interface CancellationProps {
  form: FormInstance<any>;
  devices?: any[];
  isUpload?: boolean;
}

type PenaltyRequestState = 'idle' | 'success' | 'asset_not_found' | 'error';

const useCancellation = ({
  form,
  devices,
  isUpload,
}: CancellationProps): DeviceActionProps => {
  const { t } = useTranslation([
    'actions-modal',
    'device-actions',
    'attributes',
  ]);
  const {
    selectedApp,
    isMot,
    customer: { customerLogged },
  } = useContext(SessionContext);
  const isRoot = customerLogged?.type === 'ROOT';
  const formCustomerId = Form.useWatch('customerId', form);

  const hasSelectedDevices = useMemo(
    () => !isUpload && devices && devices.length > 0,
    [devices, isUpload],
  );

  const deviceCustomerId = useMemo(
    () => (hasSelectedDevices ? devices?.[0]?.customerId : undefined),
    [hasSelectedDevices, devices],
  );

  const deviceAssetNumber = hasSelectedDevices
    ? devices?.[0]?.contractAssetNumber
    : undefined;

  const customerId = useMemo(() => {
    if (hasSelectedDevices) return deviceCustomerId;
    return isRoot ? formCustomerId : customerLogged?.id;
  }, [
    customerLogged?.id,
    deviceCustomerId,
    formCustomerId,
    hasSelectedDevices,
    isRoot,
  ]);

  // const cancellationType = Form.useWatch<string>('type', form);
  const cancellationType = 'partial' as string;
  const uploadedItems = Form.useWatch<{ msisdn: string }[]>(
    'uploadedItems',
    form,
  );
  const assetNumberParent = Form.useWatch<string>('assetNumberParent', form);

  const effectiveAssetNumber = hasSelectedDevices
    ? deviceAssetNumber
    : assetNumberParent;

  const [isRequestingPenalty, setIsRequestingPenalty] = useState(false);
  const [penaltyResponse, setPenaltyResponse] =
    useState<CancellationPenaltyResponse>();
  const [penaltyRequestState, setPenaltyRequestState] =
    useState<PenaltyRequestState>('idle');

  const consultPenalty = useCallback(
    (payload: CancellationPenaltyPayloadProps) =>
      api.post('/service-proxy/service-request/retention/penalty', payload),
    [],
  );

  const sendAction = useCallback(
    (payload: CancellationPayloadProps) =>
      api.post('/service-proxy/service-request/retention/acceptance', payload),
    [],
  );

  const { data: contracts } = useSwr<IContract[] | ''>(
    customerId ? '/service-proxy/contracts' : null,
    {
      params: {
        customerId,
        product: selectedApp?.toUpperCase(),
      },
    },
  );

  useEffect(() => {
    form.setFieldValue('uploadedItems', undefined);
    setPenaltyResponse(undefined);
    setPenaltyRequestState('idle');
  }, [assetNumberParent, form]);

  const contractOptions = useMemo(
    () =>
      Array.isArray(contracts)
        ? contracts
            .filter(item => item.flagIsca !== '1')
            .map(({ planName, assetNumber, nickname }) => ({
              value: assetNumber,
              label: `${assetNumber} ${
                nickname ? ` - ${nickname}` : ''
              } - ${planName}`,
            }))
        : [],
    [contracts],
  );

  const handleRequestingPenalty = useCallback(async () => {
    setIsRequestingPenalty(true);
    setPenaltyResponse(undefined);

    try {
      const penaltyPayload: CancellationPenaltyPayloadProps = {
        assetNumberParent: effectiveAssetNumber,
        cancelAllChildren: cancellationType === 'total',
      };

      if (cancellationType === 'partial') {
        const msisdnList = hasSelectedDevices
          ? devices?.map((item: any) => item.msisdn)
          : uploadedItems?.map(item => item.msisdn) || [];
        penaltyPayload.msisdnList = msisdnList;
      }

      const { data } = await consultPenalty(penaltyPayload);
      setPenaltyResponse(data);
      setPenaltyRequestState('success');
    } catch (error) {
      const responseData = (error as AxiosError<any>)?.response?.data;
      if (responseData?.message === 'ASSET_NOT_FOUND_IN_RMS') {
        setPenaltyRequestState('asset_not_found');
      } else {
        setPenaltyRequestState('error');
      }
      setPenaltyResponse(undefined);
    }

    setIsRequestingPenalty(false);
  }, [
    effectiveAssetNumber,
    cancellationType,
    consultPenalty,
    uploadedItems,
    hasSelectedDevices,
    devices,
  ]);

  const uploadStep = ActionsItems.Common.useUpload({
    actionKey: ActionTypes.CANCELLATION,
    form,
    onRemove: () => {
      setPenaltyResponse(undefined);
      setPenaltyRequestState('idle');
    },
  });

  const formatLoyaltyDate = (date: any) => date?.replace(/-/g, '/');

  const penaltyStepContent = useMemo<DeviceActionStepsProps['content']>(() => {
    let stepContent = (
      <>
        <p>{t('cancellation.consultPenaltyDescription')}</p>

        <Button
          type="primary"
          onClick={handleRequestingPenalty}
          disabled={
            !effectiveAssetNumber ||
            (cancellationType === 'partial' &&
              !hasSelectedDevices &&
              !uploadedItems?.length)
          }
          loading={isRequestingPenalty}
        >
          {t(
            `cancellation.${
              isRequestingPenalty ? 'consultingPenalty' : 'consultPenalty'
            }`,
          )}
        </Button>
      </>
    );

    const isPenaltySuccessState = penaltyRequestState === 'success';
    const isPenaltyErrorState = penaltyRequestState === 'error';
    const isPenaltyAssetNotFoundState =
      penaltyRequestState === 'asset_not_found';

    if (isPenaltySuccessState) {
      const hasPenalty = !!penaltyResponse?.penalty;
      stepContent = hasPenalty ? (
        <S.CancellationPenaltyContent>
          <p>{t('cancellation.estimatedPenalty')}</p>

          <CustomTag className="cancellation-penalty-value">
            {formatCurrency(penaltyResponse?.penalty?.penaltyAmount)}
          </CustomTag>

          {cancellationType === 'total' && (
            <>
              <Divider />
              <p className="cancellation-penalty-loyalty">
                {`${t('cancellation.contractLoyalty')}: `}
                <strong>
                  {`${formatLoyaltyDate(
                    penaltyResponse?.penalty?.loyaltyStartDate,
                  )} - ${formatLoyaltyDate(
                    penaltyResponse?.penalty?.loyaltyEndDate,
                  )}`}
                </strong>
              </p>
            </>
          )}
        </S.CancellationPenaltyContent>
      ) : (
        <div className="cancellation-penalty-not-found">
          <p>{t('cancellation.penaltyNotFound')}</p>
          <span>{t('cancellation.penaltyNotFoundDescription')}</span>
        </div>
      );
    } else if (isPenaltyAssetNotFoundState) {
      stepContent = <span>{t('cancellation.assetNotFound')}</span>;
    } else if (isPenaltyErrorState) {
      stepContent = <span>{t('cancellation.penaltyConsultationError')}</span>;
    }

    return (
      <S.CancellationPenaltyContainer>
        {cancellationType === 'total' && (
          <Alert
            description={t('cancellation.totalPenaltyAlert')}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {stepContent}
      </S.CancellationPenaltyContainer>
    );
  }, [
    effectiveAssetNumber,
    cancellationType,
    handleRequestingPenalty,
    hasSelectedDevices,
    isRequestingPenalty,
    penaltyRequestState,
    penaltyResponse?.penalty,
    t,
    uploadedItems?.length,
  ]);

  const steps: DeviceActionStepsProps[] = useMemo(() => {
    const defaultSteps: DeviceActionStepsProps[] = [
      // {
      //   title: (
      //     <>
      //       <span>{t('cancellation.type')}</span>
      //       <Popover
      //         content={
      //           <S.CancellationTypeDescription>
      //             <p>
      //               <strong>{`${t('cancellation.partial')}: `}</strong>
      //               {t('cancellation.partialDescription')}
      //             </p>
      //             <p>
      //               <strong>{`${t('cancellation.total')}: `}</strong>
      //               {t('cancellation.totalDescription')}
      //             </p>
      //           </S.CancellationTypeDescription>
      //         }
      //       >
      //         <Button type="link" size="small" icon={<InfoCircleOutlined />} />
      //       </Popover>
      //     </>
      //   ),
      //   name: 'type',
      //   content: (
      //     <>
      //       <Radio.Group>
      //         <Radio.Button value="partial">
      //           {t('cancellation.partial')}
      //         </Radio.Button>
      //         <Radio.Button value="total" disabled={!!hasSelectedDevices}>
      //           {t('cancellation.total')}
      //         </Radio.Button>
      //       </Radio.Group>
      //       {cancellationType === 'total' && (
      //         <Alert
      //           description={t('cancellation.totalCancellationWarning')}
      //           type="warning"
      //           showIcon
      //           style={{ marginTop: 12 }}
      //         />
      //       )}
      //     </>
      //   ),
      // },
    ];

    if (isRoot && !hasSelectedDevices) {
      defaultSteps.push({
        title: t('simcard-import.destination-client'),
        name: 'customerId',
        showWhenUpload: true,
        showItemMessage: contractOptions?.length === 0,
        itemMessage: t('none-found', {
          type: t('attributes:contract')?.toLowerCase(),
        }),
        content: <SelectCustomers />,
      });
    }

    if (!hasSelectedDevices) {
      defaultSteps.push({
        title: t('cancellation.contract'),
        name: 'assetNumberParent',
        content: (
          <Select
            options={contractOptions}
            disabled={!customerId || contracts === undefined}
            loading={!!customerId && contracts === undefined}
          />
        ),
      });
    }

    if (cancellationType === 'partial' && !hasSelectedDevices) {
      defaultSteps.push(uploadStep);
    }

    defaultSteps.push({
      title: t('cancellation.penalty'),
      name: 'penalty',
      showWhenUpload: true,
      isNotRequired: true,
      content: penaltyStepContent,
    });

    return defaultSteps;
  }, [
    cancellationType,
    contractOptions,
    contracts,
    customerId,
    hasSelectedDevices,
    isRoot,
    penaltyStepContent,
    t,
    uploadStep,
  ]);

  const sendDeviceAction = useCallback(async () => {
    try {
      const cancellationPayload: CancellationPayloadProps = {
        protocol: penaltyResponse?.protocol || '',
        cancellationType,
      };
      await sendAction(cancellationPayload);
    } catch {
      message.error(t('cancellation.penaltyConsultationError'));
    }
  }, [cancellationType, penaltyResponse?.protocol, sendAction, t]);

  return {
    steps,
    title: t(
      `device-actions:labels.${isMot ? 'mot' : 'common'}.${
        rolesConstants.CANCELLATION
      }`,
    ),
    description: t('cancellation.simcardDescription'),
    sendDeviceAction,
    disableDefaultUpload: true,
    confirmDisabled: !penaltyResponse?.protocol,
    infoMessage: isMot && (
      <p style={{ margin: 0, fontWeight: 'bold' }}>
        {t('just-algar-lines')?.toUpperCase()}
      </p>
    ),
    onClose: () => {
      setPenaltyResponse(undefined);
    },
  };
};

export default useCancellation;
